import { memo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { isEmpty } from 'lodash'

import styles from './Main.module.scss'

import ConcessionsCartSidebar from '../components/ConcessionsCartSidebar'
import CartModal from '../containers/CartModal'
import Items from '../components/Items'
import Navbar from '../containers/Navbar'

import HeaderBar from '../components/HeaderBar'

import { getOrderInProgress } from '../selectors/order'
import { getSomethingToSync } from '../VNSyncManager/Selectors'
import { setCategorySearch } from '../VNCategories/ActionCreators'
import { getActiveAttendantId, hasAttendants } from '../selectors/attendant'
import { clearRemoteOrderTotal } from '../actions/orderTotalRemote'
import { clearOrderInProgress, resetMutationInProgress } from '../actions/order'
import { isTabbed } from '../utils/orderStates'
import { clearPromotionsOnOrder } from '../actions/promotion'
import VNCategories from '../VNCategories/components/VNCategories'
import { setRoute } from '../VNRoutes/ActionCreators'
import { setShouldShowUpdateModal } from '../VNUpdateManager/ActionCreators'

const Main = ({ className }) => {
  const dispatch = useDispatch()
  const attendantsExist = useSelector(hasAttendants)
  const hasActiveAttendant = useSelector(getActiveAttendantId) !== undefined

  const classNames = cn(styles.main, className)
  const orderInProgress = useSelector(getOrderInProgress)

  useEffect(() => {
    dispatch(resetMutationInProgress())
  }, [dispatch])

  /**
   * first thing we need to do is prevent the main component to clear order in progress and total
   * for tabbed orders
   *
   * We can't use the isTabbed util here because it uses orderInProgress obj, if we add
   * orderInProgress to the dependency array we enter in a re render loop.
   * This way we follow the hooks rules and prevent endless renders loops
   */
  useEffect(() => {
    if (isTabbed(orderInProgress)) {
      return
    }

    dispatch(clearRemoteOrderTotal())

    // don't call clear if there's nothing to clear
    if (isEmpty(orderInProgress)) return

    dispatch(clearPromotionsOnOrder())
    dispatch(clearOrderInProgress())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderInProgress?.state])

  useEffect(() => {
    // redirect to login if no attendant is logged in or if route is accessed directly
    if (attendantsExist && !hasActiveAttendant) {
      dispatch(setRoute('/user-login'))
    }

    // Allow the update modal to be shown when on the main screen.
    dispatch(setShouldShowUpdateModal(true))

    // Disable the update modal when leaving the main screen.
    return () => {
      dispatch(setShouldShowUpdateModal(false))
      // clear item search query when leaving the main screen
      dispatch(setCategorySearch(''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shouldDisplayHeader = useSelector(getSomethingToSync)
  const cart =
    document?.body?.clientWidth <= 800 ? (
      <CartModal />
    ) : (
      <ConcessionsCartSidebar className={cn(shouldDisplayHeader ? styles.shortenSidebar : styles.sidebar)} />
    )

  return (
    <div className={classNames}>
      <HeaderBar />
      <Navbar className={styles.navbar} />
      {cart}
      <div className={shouldDisplayHeader ? styles.contentWithHeaderBar : styles.content}>
        <VNCategories />
        <Items className={styles.items} />
        <div className={styles.bottomGradient}></div>
      </div>
    </div>
  )
}

export default memo(Main)
